import { useState, useEffect } from "react";
import { Check, X, Mail, Phone, Building } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminInquiries() {
  const [sponsorInquiries, setSponsorInquiries] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchData = async () => {
    try {
      const [inquiriesRes, contactsRes] = await Promise.all([
        fetch(`${API}/admin/sponsor-inquiries`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }),
        fetch(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } })
      ]);

      if (inquiriesRes.ok) setSponsorInquiries(await inquiriesRes.json());
      if (contactsRes.ok) setContactSubmissions(await contactsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      const response = await fetch(`${API}/admin/sponsor-inquiries/${id}/status?status=${status}`, {
        method: "PUT",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Status updated");
        setSponsorInquiries(prev => 
          prev.map(i => i.id === id ? { ...i, status } : i)
        );
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      const response = await fetch(`${API}/admin/contacts/${id}/status?status=${status}`, {
        method: "PUT",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Status updated");
        setContactSubmissions(prev => 
          prev.map(c => c.id === id ? { ...c, status } : c)
        );
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-700";
      case "contacted": return "bg-yellow-100 text-yellow-700";
      case "resolved": return "bg-green-100 text-green-700";
      case "closed": return "bg-gray-100 text-gray-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const statusOptions = ["new", "contacted", "resolved", "closed"];

  return (
    <div className="space-y-6" data-testid="admin-inquiries">
      <h1 className="font-heading text-2xl text-kerala-green">Inquiries</h1>

      <Tabs defaultValue="sponsors">
        <TabsList>
          <TabsTrigger value="sponsors">
            Sponsor Inquiries
            {sponsorInquiries.filter(i => i.status === "new").length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-kerala-terracotta text-white text-xs rounded-full">
                {sponsorInquiries.filter(i => i.status === "new").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="contact">
            Contact Messages
            {contactSubmissions.filter(c => c.status === "new").length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-kerala-terracotta text-white text-xs rounded-full">
                {contactSubmissions.filter(c => c.status === "new").length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Sponsor Inquiries */}
        <TabsContent value="sponsors">
          <div className="bg-white rounded-sm border border-kerala-gold/10">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto" />
              </div>
            ) : sponsorInquiries.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No sponsor inquiries yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sponsorInquiries.map(inquiry => (
                    <TableRow key={inquiry.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{inquiry.company}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{inquiry.name}</p>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${inquiry.email}`} className="hover:text-kerala-terracotta">
                              {inquiry.email}
                            </a>
                          </div>
                          {inquiry.phone && (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Phone className="w-3 h-3" />
                              <span>{inquiry.phone}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-gray-600 line-clamp-2">{inquiry.message}</p>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(inquiry.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={inquiry.status}
                          onValueChange={(value) => updateInquiryStatus(inquiry.id, value)}
                        >
                          <SelectTrigger className={`w-32 ${getStatusColor(inquiry.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(status => (
                              <SelectItem key={status} value={status} className="capitalize">
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Contact Submissions */}
        <TabsContent value="contact">
          <div className="bg-white rounded-sm border border-kerala-gold/10">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto" />
              </div>
            ) : contactSubmissions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No contact messages yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactSubmissions.map(contact => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{contact.name}</p>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${contact.email}`} className="hover:text-kerala-terracotta">
                              {contact.email}
                            </a>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{contact.subject}</TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-gray-600 line-clamp-2">{contact.message}</p>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={contact.status}
                          onValueChange={(value) => updateContactStatus(contact.id, value)}
                        >
                          <SelectTrigger className={`w-32 ${getStatusColor(contact.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(status => (
                              <SelectItem key={status} value={status} className="capitalize">
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
